import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import {
  ArrowLeft,
  MapPin,
  Clock,
} from "lucide-react";
import {
  PhoneLink,
  EmailLink,
  createWhatsAppLink,
} from "@/utils/internal/linkHelpers";
import { CONTACTS_PAGE } from "@/utils/constants/pages/contacts";
import { CONTACT_INFO } from "@/utils/constants/app";

interface ContactsPageProps {
  onBackToHome: () => void;
}

export function ContactsPage({
  onBackToHome,
}: Readonly<ContactsPageProps>) {
  return (
     <div className="min-h-screen bg-white">
      {/* Header with Back Button */}
      <div className="bg-green-600 text-white py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center mb-4">
            <Button
              variant="ghost"
              onClick={onBackToHome}
              className="text-white hover:bg-green-700 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {CONTACTS_PAGE.HEADER.BACK_BUTTON}
            </Button>
          </div>
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              {CONTACTS_PAGE.HEADER.TITLE}
            </h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              {CONTACTS_PAGE.HEADER.SUBTITLE}
            </p>
          </div>
        </div>
      </div>


      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-[1320px] mx-auto px-6">
          {/* Quick Contact Methods */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 items-stretch">
            {CONTACTS_PAGE.CONTACT_METHODS.map((method) => {
              const IconComponent = method.icon;
              return (
                <Card
                  key={method.id}
                  className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center h-full flex flex-col"
                >
                  <CardContent className="p-6 flex flex-col h-full">
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${method.color}`}
                    >
                      <IconComponent className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {method.title}
                    </h3>
                    {"subtitle" in method && (
                      <p className="text-sm text-gray-600 mb-3">
                        {method.subtitle}
                      </p>
                    )}

                    {/* Clickable contact info */}
                    {method.id === "phone" && (
                      <PhoneLink
                        phoneNumber={method.primary}
                        className="text-lg font-medium text-gray-900 mb-2 hover:text-green-600"
                      />
                    )}
                    {method.id === "email" && (
                      <EmailLink
                        email={method.primary}
                        subject="Richiesta informazioni servizi"
                        className="text-lg font-medium text-gray-900 mb-2 hover:text-green-600"
                      />
                    )}
                    {method.id === "whatsapp" && (
                      <a
                        href={createWhatsAppLink(
                          method.primary,
                          CONTACT_INFO.WHATSAPP_DEFAULT_MESSAGE,
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-lg font-medium text-gray-900 mb-2 hover:text-green-600 hover:underline"
                      >
                        {method.primary}
                      </a>
                    )}
                    {method.id === "emergency" && (
                      <PhoneLink
                        phoneNumber={method.primary}
                        className="text-lg font-medium text-gray-900 mb-2 hover:text-green-600"
                      />
                    )}

                    {"secondary" in method && (
                      <p className="text-sm text-gray-600 mb-4">
                        {method.secondary}
                      </p>
                    )}
                    <p className="text-sm text-gray-600 mb-4 flex-1">
                      {method.description}
                    </p>

                    {method.id === "phone" && (
                      <PhoneLink
                        phoneNumber={method.primary}
                        className="cta-button-enhanced w-full bg-green-600 hover:bg-green-700 text-white mt-auto inline-flex items-center justify-center px-4 py-3 text-base font-medium rounded-lg transition-colors"
                      >
                        {method.action}
                      </PhoneLink>
                    )}
                    {method.id === "whatsapp" && (
                      <a
                        href={createWhatsAppLink(
                          method.primary,
                          CONTACT_INFO.WHATSAPP_DEFAULT_MESSAGE,
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cta-button-enhanced w-full bg-green-600 hover:bg-green-700 text-white mt-auto inline-flex items-center justify-center px-4 py-3 text-base font-medium rounded-lg transition-colors"
                      >
                        {method.action}
                      </a>
                    )}
                    {method.id === "emergency" && (
                      <PhoneLink
                        phoneNumber={method.primary}
                        className="cta-button-enhanced w-full bg-green-600 hover:bg-green-700 text-white mt-auto inline-flex items-center justify-center px-4 py-3 text-base font-medium rounded-lg transition-colors"
                      >
                        {method.action}
                      </PhoneLink>
                    )}
                    {method.id === "email" && (
                      <EmailLink
                        email={method.primary}
                        subject="Richiesta informazioni servizi"
                        className="cta-button-enhanced w-full bg-green-600 hover:bg-green-700 text-white mt-auto inline-flex items-center justify-center px-4 py-3 text-base font-medium rounded-lg transition-colors"
                      >
                        {method.action}
                      </EmailLink>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Office Hours */}
          <div className="mb-16">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center">
                  <Clock className="w-6 h-6 mr-3 text-green-600" />
                  {CONTACTS_PAGE.OFFICE_HOURS.TITLE}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {CONTACTS_PAGE.OFFICE_HOURS.SCHEDULE.map(
                  (schedule) => (
                    <div
                      key={schedule.day}
                      className="flex justify-between items-center p-4 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <span className="font-medium text-gray-900">
                          {schedule.day}
                        </span>
                      </div>
                      <span
                        className={`font-medium ${schedule.hours === "Chiuso" ? "text-gray-500" : "text-green-600"}`}
                      >
                        {schedule.hours}
                      </span>
                    </div>
                  ),
                )}
              </CardContent>
            </Card>
          </div>

          {/* Location & Service Area */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center">
                <MapPin className="w-6 h-6 mr-3 text-green-600" />
                {CONTACTS_PAGE.LOCATION.TITLE}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Sede Legale */}
                <div className="flex flex-col h-full">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">
                    Sede Legale
                  </h4>
                  <div className="space-y-3 flex-1">
                    <div className="text-gray-700">
                      <p>
                        Circonvallazione Clodia, 163/167
                        <br />
                        00195 Roma (RM)
                        <br />
                        Italia
                      </p>
                    </div>
                  </div>
                </div>

                {/* Sede Operativa */}
                <div className="flex flex-col h-full">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">
                    Sede Operativa
                  </h4>
                  <div className="space-y-3 flex-1">
                    <div className="text-gray-700">
                      <p>
                        Via degli Adimari, 27
                        <br />
                        00148 Roma (RM)
                        <br />
                        Italia
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}